import {
  INDIAN_POKER_ANTE,
  type IndianPokerAction,
  type IndianPokerCard,
  type IndianPokerFoldAction,
  type IndianPokerPlayer,
  type IndianPokerRaiseAction,
  type IndianPokerRound,
  type IndianPokerRoundResultReason,
  type IndianPokerState,
} from "../types";
import { createShuffledDeck } from "../utils/deck";

function findPlayer(
  state: IndianPokerState,
  playerId: string,
): IndianPokerPlayer {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }
  return player;
}

function getOpponent(
  state: IndianPokerState,
  playerId: string,
): IndianPokerPlayer {
  const opponent = state.players.find(
    (candidate) => candidate.playerId !== playerId,
  );
  if (!opponent) {
    throw new Error("Indian Poker requires two players");
  }
  return opponent;
}

function updatePlayer(
  players: readonly IndianPokerPlayer[],
  playerId: string,
  update: (player: IndianPokerPlayer) => IndianPokerPlayer,
): readonly IndianPokerPlayer[] {
  return players.map((player) =>
    player.playerId === playerId ? update(player) : player,
  );
}

function payToPot(
  players: readonly IndianPokerPlayer[],
  playerId: string,
  amount: number,
): {
  readonly players: readonly IndianPokerPlayer[];
  readonly paid: number;
} {
  const player = players.find((candidate) => candidate.playerId === playerId);
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }

  const paid = Math.min(player.chips, Math.max(0, amount));
  return {
    paid,
    players: updatePlayer(players, playerId, (current) => ({
      ...current,
      chips: current.chips - paid,
      currentBet: current.currentBet + paid,
    })),
  };
}

function transferChips(
  players: readonly IndianPokerPlayer[],
  fromPlayerId: string,
  toPlayerId: string,
  amount: number,
): readonly IndianPokerPlayer[] {
  const fromPlayer = players.find(
    (candidate) => candidate.playerId === fromPlayerId,
  );
  if (!fromPlayer) {
    throw new Error(`Unknown player: ${fromPlayerId}`);
  }

  const paid = Math.min(fromPlayer.chips, Math.max(0, amount));
  return players.map((player) => {
    if (player.playerId === fromPlayerId) {
      return { ...player, chips: player.chips - paid };
    }
    if (player.playerId === toPlayerId) {
      return { ...player, chips: player.chips + paid };
    }
    return player;
  });
}

function awardPot(
  players: readonly IndianPokerPlayer[],
  winnerPlayerId: string,
  pot: number,
): readonly IndianPokerPlayer[] {
  return updatePlayer(players, winnerPlayerId, (player) => ({
    ...player,
    chips: player.chips + pot,
  }));
}

function getRoundCards(round: IndianPokerRound): readonly IndianPokerCard[] {
  return Object.values(round.cardsByPlayerId);
}

function getWinnerAfterBankruptcy(
  players: readonly IndianPokerPlayer[],
): string | undefined {
  return players.find((player) => player.chips > 0)?.playerId;
}

function hasBankruptPlayer(players: readonly IndianPokerPlayer[]): boolean {
  return players.some((player) => player.chips <= 0);
}

function appendActedPlayerId(
  actedPlayerIds: readonly string[],
  playerId: string,
): readonly string[] {
  if (actedPlayerIds.includes(playerId)) {
    return actedPlayerIds;
  }

  return [...actedPlayerIds, playerId];
}

function canPlayerRaise(
  players: readonly IndianPokerPlayer[],
  activePlayerId: string,
): boolean {
  const activePlayer = players.find(
    (player) => player.playerId === activePlayerId,
  );
  const opponent = players.find((player) => player.playerId !== activePlayerId);
  if (!activePlayer || !opponent) {
    return false;
  }

  const minRaiseTotal = opponent.currentBet + 1;
  const maxTotalBet = Math.min(
    activePlayer.currentBet + activePlayer.chips,
    opponent.currentBet + opponent.chips,
  );
  return minRaiseTotal <= maxTotalBet;
}

interface ResolveRoundOptions {
  readonly deferGameOver?: boolean;
}

function withResolvedRound(
  state: IndianPokerState,
  players: readonly IndianPokerPlayer[],
  result: {
    readonly winnerPlayerId?: string;
    readonly foldedPlayerId?: string;
    readonly reason: IndianPokerRoundResultReason;
    readonly carryoverPot: number;
  },
  options: ResolveRoundOptions = {},
): IndianPokerState {
  const winnerAfterBankruptcy = getWinnerAfterBankruptcy(players);
  const hasUnresolvedCarryover =
    result.reason === "TIE" && result.carryoverPot > 0;
  const completed =
    !options.deferGameOver &&
    !hasUnresolvedCarryover &&
    hasBankruptPlayer(players);
  return {
    ...state,
    status: completed ? "COMPLETED" : "IN_PROGRESS",
    players,
    round: {
      ...state.round,
      phase: completed ? "GAME_OVER" : "ROUND_RESULT",
      winnerPlayerId: result.winnerPlayerId,
      foldedPlayerId: result.foldedPlayerId,
      resultReason: result.reason,
      carryoverPot: result.carryoverPot,
      confirmedPlayerIds: [],
    },
    winnerPlayerId: completed ? winnerAfterBankruptcy : undefined,
  };
}

function drawCard(deck: readonly IndianPokerCard[]): {
  readonly card: IndianPokerCard;
  readonly deck: readonly IndianPokerCard[];
} {
  const [card, ...remainingDeck] = deck;
  if (!card) {
    throw new Error("Cannot draw from an empty deck");
  }
  return { card, deck: remainingDeck };
}

function resetPlayersForNextRound(
  players: readonly IndianPokerPlayer[],
): readonly IndianPokerPlayer[] {
  return players.map((player) => ({ ...player, currentBet: 0 }));
}

function collectAntes(
  players: readonly IndianPokerPlayer[],
  carryoverPot: number,
): {
  readonly players: readonly IndianPokerPlayer[];
  readonly pot: number;
} {
  let nextPlayers = resetPlayersForNextRound(players);
  let pot = carryoverPot;

  for (const player of players) {
    const payment = payToPot(nextPlayers, player.playerId, INDIAN_POKER_ANTE);
    nextPlayers = payment.players;
    pot += payment.paid;
  }

  return { players: nextPlayers, pot };
}

function startNextRound(
  state: IndianPokerState,
  priorityPlayerId: string,
  carryoverPot: number,
): IndianPokerState {
  const spentCards = getRoundCards(state.round);
  const needsFreshDeck = state.deck.length < state.players.length;
  const deckSetup = needsFreshDeck
    ? createShuffledDeck(state.deckGeneration + 1, state.rngState)
    : { deck: state.deck, rngState: state.rngState };

  const firstDeal = drawCard(deckSetup.deck);
  const secondDeal = drawCard(firstDeal.deck);
  const firstPlayer = state.players[0];
  const secondPlayer = state.players[1];
  if (!firstPlayer || !secondPlayer) {
    throw new Error("Indian Poker requires two players");
  }

  const ante = collectAntes(state.players, carryoverPot);

  const nextState: IndianPokerState = {
    ...state,
    players: ante.players,
    round: {
      roundNumber: state.round.roundNumber + 1,
      phase: "BETTING",
      activePlayerId: priorityPlayerId,
      bettingPriorityPlayerId: priorityPlayerId,
      pot: ante.pot,
      carryoverPot,
      actedPlayerIds: [],
      cardsByPlayerId: {
        [firstPlayer.playerId]: firstDeal.card,
        [secondPlayer.playerId]: secondDeal.card,
      },
      confirmedPlayerIds: [],
    },
    completedRounds: [...state.completedRounds, state.round],
    deck: secondDeal.deck,
    discardPile: needsFreshDeck ? [] : [...state.discardPile, ...spentCards],
    deckGeneration: needsFreshDeck
      ? state.deckGeneration + 1
      : state.deckGeneration,
    rngState: deckSetup.rngState,
    winnerPlayerId: undefined,
  };

  return resolveShowdownIfNextPlayerCannotRaise(
    nextState,
    ante.players,
    priorityPlayerId,
  );
}

function resolveShowdown(
  state: IndianPokerState,
  players: readonly IndianPokerPlayer[],
  options: ResolveRoundOptions = {},
): IndianPokerState {
  const firstPlayer = state.players[0];
  const secondPlayer = state.players[1];
  if (!firstPlayer || !secondPlayer) {
    throw new Error("Indian Poker requires two players");
  }

  const firstCard = state.round.cardsByPlayerId[firstPlayer.playerId];
  const secondCard = state.round.cardsByPlayerId[secondPlayer.playerId];
  if (!firstCard || !secondCard) {
    throw new Error("Round cards are missing");
  }

  if (firstCard.value === secondCard.value) {
    return withResolvedRound(
      state,
      players,
      {
        reason: "TIE",
        carryoverPot: state.round.pot,
      },
      options,
    );
  }

  const winnerPlayerId =
    firstCard.value > secondCard.value
      ? firstPlayer.playerId
      : secondPlayer.playerId;
  return withResolvedRound(
    state,
    awardPot(players, winnerPlayerId, state.round.pot),
    {
      winnerPlayerId,
      reason: "HIGH_CARD",
      carryoverPot: 0,
    },
    options,
  );
}

function resolveShowdownIfNextPlayerCannotRaise(
  state: IndianPokerState,
  players: readonly IndianPokerPlayer[],
  nextActivePlayerId: string,
): IndianPokerState {
  if (canPlayerRaise(players, nextActivePlayerId)) {
    return state;
  }

  const nextActivePlayer = players.find(
    (player) => player.playerId === nextActivePlayerId,
  );
  const opponent = players.find(
    (player) => player.playerId !== nextActivePlayerId,
  );
  if (!nextActivePlayer || !opponent) {
    throw new Error("Indian Poker requires two players");
  }

  const requiredMatch = Math.max(
    0,
    opponent.currentBet - nextActivePlayer.currentBet,
  );
  const payment = payToPot(players, nextActivePlayer.playerId, requiredMatch);

  return resolveShowdown(
    {
      ...state,
      players: payment.players,
      round: {
        ...state.round,
        pot: state.round.pot + payment.paid,
        actedPlayerIds: appendActedPlayerId(
          state.round.actedPlayerIds,
          nextActivePlayer.playerId,
        ),
      },
    },
    payment.players,
    { deferGameOver: true },
  );
}

export function applyCall(
  state: IndianPokerState,
  playerId: string,
): IndianPokerState {
  const activePlayer = findPlayer(state, playerId);
  const opponent = getOpponent(state, activePlayer.playerId);
  const requiredMatch = Math.max(
    0,
    opponent.currentBet - activePlayer.currentBet,
  );
  const payment = payToPot(state.players, activePlayer.playerId, requiredMatch);

  return resolveShowdown(
    {
      ...state,
      players: payment.players,
      round: {
        ...state.round,
        pot: state.round.pot + payment.paid,
        actedPlayerIds: appendActedPlayerId(
          state.round.actedPlayerIds,
          activePlayer.playerId,
        ),
      },
    },
    payment.players,
  );
}

export function applyRaise(
  state: IndianPokerState,
  action: IndianPokerRaiseAction,
): IndianPokerState {
  const activePlayer = findPlayer(state, action.playerId);
  const opponent = getOpponent(state, activePlayer.playerId);
  const raiseAmount = action.totalBet - activePlayer.currentBet;
  const payment = payToPot(state.players, activePlayer.playerId, raiseAmount);

  const nextState = {
    ...state,
    players: payment.players,
    round: {
      ...state.round,
      activePlayerId: opponent.playerId,
      pot: state.round.pot + payment.paid,
      actedPlayerIds: [activePlayer.playerId],
    },
  };

  return nextState;
}

export function applyFold(
  state: IndianPokerState,
  action: IndianPokerFoldAction,
): IndianPokerState {
  const foldedPlayer = findPlayer(state, action.playerId);
  const opponent = getOpponent(state, foldedPlayer.playerId);
  const foldedCard = state.round.cardsByPlayerId[foldedPlayer.playerId];
  if (!foldedCard) {
    throw new Error("Folded player's card is missing");
  }

  const reason =
    foldedCard.value === 10 ? "TEN_FOLD_PENALTY" : ("FOLD" as const);
  const withPotAwarded = awardPot(
    state.players,
    opponent.playerId,
    state.round.pot,
  );
  const players =
    reason === "TEN_FOLD_PENALTY"
      ? transferChips(
          withPotAwarded,
          foldedPlayer.playerId,
          opponent.playerId,
          10,
        )
      : withPotAwarded;

  return withResolvedRound(state, players, {
    winnerPlayerId: opponent.playerId,
    foldedPlayerId: foldedPlayer.playerId,
    reason,
    carryoverPot: 0,
  });
}

export function applyConfirmRoundResult(
  state: IndianPokerState,
  playerId: string,
): IndianPokerState {
  const confirmedPlayerIds = [...state.round.confirmedPlayerIds, playerId];
  const round = { ...state.round, confirmedPlayerIds };
  const allConfirmed = state.players.every((player) =>
    confirmedPlayerIds.includes(player.playerId),
  );

  if (!allConfirmed) {
    return { ...state, round };
  }

  if (
    round.resultReason !== undefined &&
    round.resultReason !== "TIE" &&
    hasBankruptPlayer(state.players)
  ) {
    return {
      ...state,
      status: "COMPLETED",
      round: {
        ...round,
        phase: "GAME_OVER",
      },
      winnerPlayerId:
        round.winnerPlayerId ?? getWinnerAfterBankruptcy(state.players),
    };
  }

  const priorityPlayerId =
    round.winnerPlayerId ?? round.bettingPriorityPlayerId;
  const carryoverPot = round.resultReason === "TIE" ? round.carryoverPot : 0;
  return startNextRound({ ...state, round }, priorityPlayerId, carryoverPot);
}

export function applyAction(
  state: IndianPokerState,
  action: IndianPokerAction,
): IndianPokerState {
  if (action.type === "CALL") return applyCall(state, action.playerId);
  if (action.type === "RAISE") return applyRaise(state, action);
  if (action.type === "FOLD") return applyFold(state, action);
  if (action.type === "CONFIRM_ROUND_RESULT") {
    return applyConfirmRoundResult(state, action.playerId);
  }
  return state;
}
